/**
 * Asset ↔ project assignment history — reads + writes through the Hono backend
 * (`apps/api/src/routes/projects/asset-assignments.ts`, `/api/v1/asset-assignments/…`,
 * mounted outside the typed chain, so the `authed*` helpers are the client).
 *
 * A project keeps a permanent record of every asset it used: one
 * `project_asset_assignments` row per use, carrying that use's quantity and
 * LCA facts (the six per-use fields). Return closes a row (history kept);
 * remove deletes one that was added by mistake (docs/MATERIALS.md).
 *
 * Liveness contract: every read keys under `['asset-assignments', orgId]` —
 *   project uses  `['asset-assignments', orgId, 'project', projectId]`
 *   asset history `['asset-assignments', orgId, 'asset', assetId]`
 * — and every mutation invalidates that prefix AND `['assets', orgId]`
 * (assign/return/remove move the asset's pointer and status, which the org
 * list, the picker and the asset detail all read).
 *
 * Errors: `onError` toasts only — the app-level `MutationCache.onError`
 * captures to Sentry, except for the business-rule refusals listed in
 * {@link ASSET_ASSIGNMENT_MUTATION_META} (a retired asset, a closed project…),
 * which are user-facing rules, not bugs. Pass `quiet: true` in a mutation's
 * variables when the caller owns the toasts — the hook then stays silent on
 * success AND failure (e.g. the grid save shows one `materials.saved`, and
 * the assign-after-create step its own `materials.assignAfterCreateFailed`).
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  AssetAssignmentFailureCode,
  AssetAssignmentHistoryResponse,
  AssetAssignmentHistoryRowWire,
  AssignAssetsResponse,
  AssignItem,
  PatchBody,
  ProjectAssetUseWire,
  ProjectAssetUsesResponse,
  RemoveAssetAssignmentResponse,
  RequestableReturnAction,
  ReturnAction,
  ReturnAssetsResponse,
} from '@releve/shared';
import i18n from '@/i18n';
import { toast } from '@/hooks/useToast';
import { auditKeys } from '@/lib/auditCacheKeys';
import { authedDelete, authedGet, authedPatch, authedPost } from '@/lib/api';
import type { AssetListItem } from '@/hooks/useAssets';

export type ProjectAssetUse = ProjectAssetUseWire;
export type AssetAssignmentHistoryRow = AssetAssignmentHistoryRowWire;

/** Query-key slot 0 — the prefix every mutation here invalidates. */
export const ASSET_ASSIGNMENTS_QUERY_KEY = 'asset-assignments';

/** `/api/v1/asset-assignments/orgs/:orgId` — every route hangs off it. */
export const assetAssignmentsOrgPath = (organizationId: string) =>
  `/api/v1/asset-assignments/orgs/${organizationId}`;

// ---------------------------------------------------------------------------
// Grid row
// ---------------------------------------------------------------------------

/**
 * The flat Materials grid row (replaces the table's old `assetToRow`), shared
 * by the project view (one row per USE) and the org-wide view (one row per
 * ASSET). A `type` alias, not an interface, so it is assignable to the
 * table's `Record<string, unknown>` row shape.
 *
 * - Project view: `id` = the assignment id, `asset_id` the asset (NULL once
 *   the asset is deleted — `code` / `name` then fall back to the snapshot).
 * - Global view: `id` = `asset_id` = the asset id, `open_assignment_id` =
 *   the asset's open assignment (for Return). Per-use fields come from that
 *   open use (`per_use_source: 'use'`) or, for a thing on no project, from
 *   the thing's own defaults (`'default'`, `quantity_used` NULL).
 */
export type MaterialRow = {
  id: string;
  _rowId: string;
  asset_id: string | null;
  /** The open assignment behind this row: its own id when open (project view), the asset's open use (global view). */
  open_assignment_id: string | null;
  /** The project this use belongs to (project view) / the asset's current project (global view). */
  project_id: string | null;
  organization_id: string;
  // Asset-intrinsic (live asset, or the snapshot when it was deleted)
  code: string;
  name: string;
  description: string | null;
  asset_type_id: string | null;
  asset_types: { id: string; code: string; name: string; icon: string | null; color: string | null } | null;
  status: string | null;
  business_id: string | null;
  business_name: string | null;
  location_name: string | null;
  recycled_content_id: string | null;
  purchase_value: number | null;
  current_value: number | null;
  purchase_date: string | null;
  purchase_expense_id: string | null;
  purchase_line_item_id: string | null;
  created_at: string | null;
  updated_at: string | null;
  /**
   * Where the six per-use values came from: `'use'` = a project use (always in
   * the project view; the open use in the global view); `'default'` = the
   * thing's own defaults (global view, no project — quantity has none).
   */
  per_use_source: 'use' | 'default';
  // Per use
  quantity_used: number | null;
  unit_code: string | null;
  procurement_type_id: string | null;
  end_of_life_plan_id: string | null;
  waste_treatment_method_id: string | null;
  sustainability_notes: string | null;
  // The use's lifetime (NULL in the global view)
  assigned_on: string | null;
  returned_on: string | null;
  return_action: ReturnAction | null;
  // The asset's current project ("Currently on"). Project view: this project
  // while the use is open, otherwise unknown (NULL).
  assigned_project_id: string | null;
  project_name: string | null;
  project_code: string | null;
};

/**
 * One project use → a grid row. `project` (optional) names the viewed
 * project for the row's "Currently on" fields while the use is open.
 */
export function assetUseToMaterialRow(
  use: ProjectAssetUse,
  project?: { name: string | null; code: string | null } | null,
): MaterialRow {
  const asset = use.asset;
  const isOpen = use.returned_on === null;
  return {
    id: use.id,
    _rowId: use.id,
    asset_id: use.asset_id,
    open_assignment_id: isOpen ? use.id : null,
    project_id: use.project_id,
    organization_id: use.organization_id,
    code: asset?.code ?? use.asset_code,
    name: asset?.name ?? use.asset_name,
    description: asset?.description ?? null,
    asset_type_id: asset?.asset_type_id ?? null,
    asset_types: asset?.asset_types ?? null,
    status: asset?.status ?? null,
    business_id: asset?.business_id ?? null,
    business_name: asset?.businesses?.name ?? null,
    location_name: asset?.locations?.name ?? null,
    recycled_content_id: asset?.recycled_content_id ?? null,
    purchase_value: asset?.purchase_value ?? null,
    current_value: asset?.current_value ?? null,
    purchase_date: asset?.purchase_date ?? null,
    purchase_expense_id: asset?.purchase_expense_id ?? null,
    purchase_line_item_id: asset?.purchase_line_item_id ?? null,
    created_at: asset?.created_at ?? null,
    updated_at: asset?.updated_at ?? null,
    per_use_source: 'use',
    quantity_used: use.quantity_used,
    unit_code: use.unit_code,
    procurement_type_id: use.procurement_type_id,
    end_of_life_plan_id: use.end_of_life_plan_id,
    waste_treatment_method_id: use.waste_treatment_method_id,
    sustainability_notes: use.sustainability_notes,
    assigned_on: use.assigned_on,
    returned_on: use.returned_on,
    return_action: use.return_action,
    assigned_project_id: isOpen ? use.project_id : null,
    project_name: isOpen ? (project?.name ?? null) : null,
    project_code: isOpen ? (project?.code ?? null) : null,
  };
}

/**
 * One org asset (the `useAssets` list) → a global-view grid row. On a project
 * the per-use columns show that project's open use; otherwise the thing's
 * defaults (no quantity).
 */
export function assetToMaterialRow(asset: AssetListItem): MaterialRow {
  const use = asset.open_use;
  return {
    id: asset.id,
    _rowId: asset.id,
    asset_id: asset.id,
    open_assignment_id: asset.open_assignment_id,
    project_id: asset.assigned_project_id ?? null,
    organization_id: asset.organization_id,
    code: asset.code,
    name: asset.name,
    description: asset.description ?? null,
    asset_type_id: asset.asset_type_id ?? null,
    asset_types: asset.asset_types
      ? {
          id: asset.asset_types.id,
          code: asset.asset_types.code,
          name: asset.asset_types.name,
          icon: asset.asset_types.icon ?? null,
          color: asset.asset_types.color ?? null,
        }
      : null,
    status: asset.status ?? null,
    business_id: asset.business_id ?? null,
    business_name: asset.businesses?.name ?? null,
    location_name: asset.locations?.name ?? null,
    recycled_content_id: asset.recycled_content_id ?? null,
    purchase_value: asset.purchase_value ?? null,
    current_value: asset.current_value ?? null,
    purchase_date: asset.purchase_date ?? null,
    purchase_expense_id: asset.purchase_expense_id ?? null,
    purchase_line_item_id: asset.purchase_line_item_id ?? null,
    created_at: asset.created_at ?? null,
    updated_at: asset.updated_at ?? null,
    ...(use
      ? {
          per_use_source: 'use' as const,
          quantity_used: use.quantity_used,
          unit_code: use.unit_code,
          procurement_type_id: use.procurement_type_id,
          end_of_life_plan_id: use.end_of_life_plan_id,
          waste_treatment_method_id: use.waste_treatment_method_id,
          sustainability_notes: use.sustainability_notes,
        }
      : {
          per_use_source: 'default' as const,
          quantity_used: null,
          unit_code: asset.unit_code ?? null,
          procurement_type_id: asset.procurement_type_id ?? null,
          end_of_life_plan_id: asset.end_of_life_plan_id ?? null,
          waste_treatment_method_id: asset.waste_treatment_method_id ?? null,
          sustainability_notes: asset.sustainability_notes ?? null,
        }),
    assigned_on: null,
    returned_on: null,
    return_action: null,
    assigned_project_id: asset.assigned_project_id ?? null,
    project_name: asset.projects?.name ?? null,
    project_code: asset.projects?.code ?? null,
  };
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/**
 * Every use of every asset on a project — open and closed — with the embedded
 * asset fields the Materials grid needs. Ordered by the API: open first, then
 * closed by `assigned_on` desc.
 */
export const useProjectAssetUses = (organizationId?: string, projectId?: string) =>
  useQuery({
    queryKey: [ASSET_ASSIGNMENTS_QUERY_KEY, organizationId, 'project', projectId],
    queryFn: async (): Promise<ProjectAssetUse[]> => {
      if (!organizationId || !projectId) return [];
      const result = await authedGet<ProjectAssetUsesResponse>(
        `${assetAssignmentsOrgPath(organizationId)}/projects/${projectId}`,
      );
      return result.uses;
    },
    enabled: !!organizationId && !!projectId,
    // Feeds an editable grid — a focus refetch would wipe in-flight cell
    // edits (EditableTable's INIT). Every write here invalidates the prefix.
    staleTime: 2 * 60 * 1000,
  });

/** An asset's project history (every assignment, with project + end-of-life plan names). */
export const useAssetAssignmentHistory = (organizationId?: string, assetId?: string) =>
  useQuery({
    queryKey: [ASSET_ASSIGNMENTS_QUERY_KEY, organizationId, 'asset', assetId],
    queryFn: async (): Promise<AssetAssignmentHistoryRow[]> => {
      if (!organizationId || !assetId) return [];
      const result = await authedGet<AssetAssignmentHistoryResponse>(
        `${assetAssignmentsOrgPath(organizationId)}/assets/${assetId}`,
      );
      return result.history;
    },
    enabled: !!organizationId && !!assetId,
    staleTime: 2 * 60 * 1000,
  });

// ---------------------------------------------------------------------------
// Invalidation
// ---------------------------------------------------------------------------

/**
 * Everything an assignment write can move: the assignment reads, the org's
 * asset caches (list + selector, both under `['assets', orgId]`), the asset
 * detail (`['asset', orgId, id]`) and the org's History reads (`['audit', orgId]` —
 * trigger-written rows).
 * Returns the refetch promise so mutations stay pending until fresh rows land.
 */
export const invalidateAssetAssignmentCaches = (queryClient: QueryClient, organizationId: string) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: [ASSET_ASSIGNMENTS_QUERY_KEY, organizationId] }),
    queryClient.invalidateQueries({ queryKey: ['assets', organizationId] }),
    queryClient.invalidateQueries({ queryKey: ['asset', organizationId] }),
    queryClient.invalidateQueries({ queryKey: auditKeys.org(organizationId) }),
  ]);

// ---------------------------------------------------------------------------
// Refusals
// ---------------------------------------------------------------------------

/** Every refusal code the assignment routes return (`{error, code, asset_id?}`). */
export const ASSET_ASSIGNMENT_REFUSAL_CODES: readonly AssetAssignmentFailureCode[] = [
  'not_found',
  'cross_org',
  'duplicate_item',
  'asset_retired',
  'not_open',
  'project_closed',
  'date_before_assignment',
];

/**
 * `meta` for the assignment mutations: App.tsx's `MutationCache.onError`
 * skips Sentry for these codes (the `VAT_DECLARED_MUTATION_META` precedent).
 * Only the business-rule refusals — `not_found` / `cross_org` stay reported,
 * since a stale or foreign id reaching the API is worth seeing.
 */
export const ASSET_ASSIGNMENT_MUTATION_META = {
  expectedErrorCodes: [
    'asset_retired',
    'not_open',
    'project_closed',
    'date_before_assignment',
    'duplicate_item',
  ] as readonly string[],
} as const;

type RefusalEnvelope = { code?: unknown; asset_id?: unknown };

const envelopeOf = (error: unknown): RefusalEnvelope | undefined =>
  (error as { envelope?: RefusalEnvelope } | null | undefined)?.envelope;

/** The coded refusal carried by a failed assignment call (`err.envelope.code`), or `null`. */
export function assignmentRefusalCode(error: unknown): AssetAssignmentFailureCode | null {
  const code = envelopeOf(error)?.code;
  return typeof code === 'string' &&
    (ASSET_ASSIGNMENT_REFUSAL_CODES as readonly string[]).includes(code)
    ? (code as AssetAssignmentFailureCode)
    : null;
}

/** The asset a per-item refusal names (`err.envelope.asset_id`), or `null`. */
export function assignmentRefusalAssetId(error: unknown): string | null {
  const assetId = envelopeOf(error)?.asset_id;
  return typeof assetId === 'string' ? assetId : null;
}

/**
 * Failure toast. A coded refusal gets its own words (`materials.refused.<code>`),
 * with the refused row's name as the description when `nameOf` resolves the
 * envelope's `asset_id`; anything else shows `fallbackKey` + the error text.
 */
export function toastAssignmentError(
  error: unknown,
  fallbackKey: string,
  nameOf?: (assetId: string) => string | null | undefined,
): void {
  const code = assignmentRefusalCode(error);
  if (code) {
    const assetId = assignmentRefusalAssetId(error);
    const name = assetId && nameOf ? nameOf(assetId) : null;
    toast.error(i18n.t(`materials.refused.${code}`), name ? { description: name } : undefined);
    return;
  }
  const description = error instanceof Error && error.message ? error.message : undefined;
  toast.error(i18n.t(fallbackKey), description ? { description } : undefined);
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

interface QuietOption {
  /** The caller owns the toasts — the hook stays silent on success and failure. */
  quiet?: boolean;
}

export interface AssignAssetsArgs extends QuietOption {
  organizationId: string;
  projectId: string;
  /** `{asset_id, ...per-use}`; an asset open on another project is transferred. */
  items: AssignItem[];
  /** `YYYY-MM-DD`; omitted = org-today, server-side. */
  assignedOn?: string;
}

/**
 * Assign assets to a project (idempotent for an asset already open there; an
 * asset open elsewhere is closed as `transferred` first). One call per save,
 * however many items.
 */
export const useAssignAssets = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [ASSET_ASSIGNMENTS_QUERY_KEY, 'assign'],
    meta: ASSET_ASSIGNMENT_MUTATION_META,
    mutationFn: ({ organizationId, projectId, items, assignedOn }: AssignAssetsArgs) =>
      authedPost<AssignAssetsResponse>(
        `${assetAssignmentsOrgPath(organizationId)}/projects/${projectId}`,
        assignedOn ? { items, assigned_on: assignedOn } : { items },
      ),
    onSuccess: (_, { organizationId }) => invalidateAssetAssignmentCaches(queryClient, organizationId),
    onError: (error, { quiet }) => {
      if (!quiet) toastAssignmentError(error, 'materials.saveFailed');
    },
  });
};

export interface PatchAssetUseArgs extends QuietOption {
  organizationId: string;
  /** The assignment id. */
  id: string;
  /** The six per-use fields only (null clears one). Closed uses stay editable. */
  patch: PatchBody;
}

export const usePatchAssetUse = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [ASSET_ASSIGNMENTS_QUERY_KEY, 'patch'],
    meta: ASSET_ASSIGNMENT_MUTATION_META,
    mutationFn: ({ organizationId, id, patch }: PatchAssetUseArgs) =>
      authedPatch<{ assignment: Omit<ProjectAssetUse, 'asset'> }>(
        `${assetAssignmentsOrgPath(organizationId)}/${id}`,
        patch,
      ),
    onSuccess: (_, { organizationId }) => invalidateAssetAssignmentCaches(queryClient, organizationId),
    onError: (error, { quiet }) => {
      if (!quiet) toastAssignmentError(error, 'materials.saveFailed');
    },
  });
};

export interface ReturnAssetsArgs extends QuietOption {
  organizationId: string;
  /** Assignment ids; rows no longer open come back as `skipped`, untouched. */
  ids: string[];
  action: RequestableReturnAction;
  /** `YYYY-MM-DD`; omitted = org-today, server-side. */
  returnedOn?: string;
}

/**
 * Close open assignments (returned / stored / retired). History is kept. The
 * success toast counts what was returned; the caller reads `skipped` off the
 * result when it wants to say more.
 */
export const useReturnAssets = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [ASSET_ASSIGNMENTS_QUERY_KEY, 'return'],
    meta: ASSET_ASSIGNMENT_MUTATION_META,
    mutationFn: ({ organizationId, ids, action, returnedOn }: ReturnAssetsArgs) =>
      authedPost<ReturnAssetsResponse>(
        `${assetAssignmentsOrgPath(organizationId)}/return`,
        returnedOn ? { ids, action, returned_on: returnedOn } : { ids, action },
      ),
    onSuccess: async (result, { organizationId, quiet }) => {
      await invalidateAssetAssignmentCaches(queryClient, organizationId);
      if (!quiet) toast.success(i18n.t('materials.returnSuccess', { count: result.returned.length }));
    },
    onError: (error, { quiet }) => {
      if (!quiet) toastAssignmentError(error, 'materials.returnFailed');
    },
  });
};

export interface RemoveAssetAssignmentArgs extends QuietOption {
  organizationId: string;
  /** The assignment id — refused unless open and the project is still open. */
  id: string;
}

/** Remove an assignment added by mistake (deletes the row; the `_audit` trail keeps it). */
export const useRemoveAssetAssignment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [ASSET_ASSIGNMENTS_QUERY_KEY, 'remove'],
    meta: ASSET_ASSIGNMENT_MUTATION_META,
    mutationFn: ({ organizationId, id }: RemoveAssetAssignmentArgs) =>
      authedDelete<RemoveAssetAssignmentResponse>(`${assetAssignmentsOrgPath(organizationId)}/${id}`),
    onSuccess: (_, { organizationId }) => invalidateAssetAssignmentCaches(queryClient, organizationId),
    onError: (error, { quiet }) => {
      if (!quiet) toastAssignmentError(error, 'materials.saveFailed');
    },
  });
};
